import { useEffect, useRef, useState } from 'react';
import { callApi } from '../../utils/apiCall';

// PieHost.generate_jwt issues 60-minute credentials. Renew before reconnects
// can encounter expiry; transient failures get two bounded retries.
const REFRESH_MS = 55 * 60 * 1000;
const RETRY_MS = [1000, 3000];

export default function useVoiceCredential({ isAuthenticated, userId, practiceId }) {
    const [credential, setCredential] = useState(null);
    const key = isAuthenticated && userId && practiceId ? `${userId}:${practiceId}` : null;
    const requestContext = useRef({ key, version: 0 });
    if (requestContext.current.key !== key) {
        requestContext.current = { key, version: requestContext.current.version + 1 };
    }
    const { version } = requestContext.current;
    useEffect(() => {
        if (!key) return undefined;
        let current = true;
        let timer;
        const load = async (attempt = 0) => {
            let response;
            try {
                response = await callApi(`/api/v1/practices/${practiceId}/calls/realtime-credential/`, 'post');
            } catch (_) { /* Network failures follow the same bounded retry path. */ }
            if (!current) return;
            const data = response?.data;
            if (response?.status === 200 && typeof data?.auth === 'string' && data.auth
                && typeof data?.room === 'string' && data.room && data.event === 'new-notification') {
                setCredential({ key, version, auth: data.auth, room: data.room });
                timer = setTimeout(() => load(), REFRESH_MS);
                return;
            }
            // Permission failures and older backends are definitive for this
            // context. Neither patient data nor tokens are logged on failures.
            if ((!response || response.status === 0 || response.status >= 500) && attempt < RETRY_MS.length) {
                timer = setTimeout(() => load(attempt + 1), RETRY_MS[attempt]);
            } else {
                setCredential(null);
            }
        };
        load();
        return () => { current = false; clearTimeout(timer); };
    }, [key, version, practiceId]);
    return key && credential?.key === key && credential.version === version ? credential : null;
}
